import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  OnDestroy,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzGridComponent } from '@template-ui/components/ez-grid/ez-grid.component';
import type { ColDef, GridOptions } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { RoleService } from '@app/shared/services/roles.service';
import {
  mapWorkflowRegistryDiagnosticError,
  type WorkflowRegistryDiagnosticDisplayError,
} from '../workflow-registry-diagnostics/platform-workflow-registry-diagnostics.models';
import { PlatformBusinessCalendarsService } from './platform-business-calendars.service';
import {
  calendarIdentity,
  calendarTimeInput,
  type BusinessCalendar,
  type BusinessCalendarDefinition,
  type BusinessCalendarDraft,
  type CalendarCalculationRequest,
  type CalendarCalculationResult,
  type CalendarOperation,
} from './platform-business-calendars.models';
import { BusinessCalendarDraftStateService } from './business-calendar-draft-state.service';

const dateShape = /^\d{4}-\d{2}-\d{2}$/;
const instantShape =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?(?:Z|[+-]\d{2}:\d{2})$/;
const weekdays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

@Component({
  selector: 'phms-platform-business-calendars',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzGridComponent,
  ],
  templateUrl: './platform-business-calendars.component.html',
  styleUrl: './platform-business-calendars.component.scss',
})
export class PlatformBusinessCalendarsComponent implements OnDestroy {
  private readonly api = inject(PlatformBusinessCalendarsService);
  private readonly roles = inject(RoleService);
  private readonly draftState = inject(BusinessCalendarDraftStateService);
  readonly canRead = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.read',
  );
  readonly canAuthor = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.author',
  );
  readonly canPublish = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.publish',
  );
  readonly weekdays = weekdays;
  drafts: BusinessCalendarDraft[] = [];
  draft: BusinessCalendarDraft | null = null;
  draftDefinition: BusinessCalendarDefinition | null = null;
  holidayDate = '';
  timeZones: { id: string; label: string }[] = [];
  newCalendarName = '';
  showCreate = false;
  loadingDrafts = false;
  draftBusy = false;
  draftStale = false;
  draftError: WorkflowRegistryDiagnosticDisplayError | null = null;
  draftValidationError = '';
  draftNotice = '';
  private mutationIntent: { fingerprint: string; key: string } | null = null;
  private draftBaseline = '';
  private draftsRequest?: Subscription;
  private mutationRequest?: Subscription;
  private draftsGeneration = 0;
  private catalogRequest?: Subscription;
  private calculationRequest?: Subscription;
  private catalogGeneration = 0;
  private calculationGeneration = 0;
  private holidayRowsSource = '';
  private cachedHolidayRows: { date: string }[] = [];
  calendars: BusinessCalendar[] = [];
  selectedKey = '';
  operation: CalendarOperation = 'add-working-days';
  date = '';
  time = '';
  instant = '';
  amount: number | null = 1;
  explicitOffsetMinutes: number | null = null;
  loadingCatalog = false;
  calculating = false;
  accessDenied = false;
  staleSource = false;
  catalogError: WorkflowRegistryDiagnosticDisplayError | null = null;
  calculationError: WorkflowRegistryDiagnosticDisplayError | null = null;
  validationError = '';
  notice = '';
  result: CalendarCalculationResult | null = null;
  readonly operations: { code: CalendarOperation; label: string }[] = [
    { code: 'add-working-days', label: 'Add working days · date only' },
    { code: 'add-calendar-days', label: 'Add calendar days · date only' },
    {
      code: 'local-to-instant',
      label: 'Local date and time → scheduled instant',
    },
    {
      code: 'instant-to-local',
      label: 'Scheduled instant → local date and time',
    },
  ];
  readonly holidayColumns: ColDef<{ date: string }>[] = [
    {
      field: 'date',
      headerName: 'Holiday date (date only)',
      flex: 1,
      minWidth: 180,
      sortable: true,
      filter: false,
    },
  ];
  readonly holidayOptions: GridOptions<{ date: string }> = {
    domLayout: 'autoHeight',
    getRowId: ({ data }) => data.date,
    suppressCellFocus: false,
  };

  constructor() {
    effect(() => {
      const allowed = this.canRead();
      untracked(() => {
        if (allowed) {
          this.refreshCatalog();
          this.refreshDrafts();
        } else this.clearProtectedData();
      });
    });
  }
  ngOnDestroy(): void {
    this.catalogGeneration++;
    this.catalogRequest?.unsubscribe();
    this.invalidateCalculation();
    this.draftsGeneration++;
    this.draftsRequest?.unsubscribe();
    this.mutationRequest?.unsubscribe();
    this.draftState.setDirty(false);
    this.draftState.setOperationInProgress(false);
  }
  get openDrafts(): BusinessCalendarDraft[] {
    return this.drafts.filter((draft) => draft.statusCode === 'draft');
  }
  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.draftState.isNavigationBlocked()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  selectDraft(id: number | string): void {
    const selected = this.openDrafts.find((draft) => draft.id === Number(id));
    if (selected) void this.openDraft(selected);
  }
  get draftDirty(): boolean {
    return this.draftDefinition
      ? this.draftInputIdentity() !== this.draftBaseline
      : this.showCreate && !!this.newCalendarName.trim();
  }
  get canEditDraft(): boolean {
    return (
      this.canRead() &&
      !this.accessDenied &&
      this.canAuthor() &&
      this.draft?.statusCode === 'draft' &&
      !this.draftBusy &&
      !this.loadingDrafts
    );
  }
  get canSaveDraft(): boolean {
    return this.canEditDraft && this.draftDirty && !this.draftStale;
  }
  get canPublishDraft(): boolean {
    return (
      this.canRead() &&
      !this.accessDenied &&
      this.canPublish() &&
      this.draft?.statusCode === 'draft' &&
      !this.draftDirty &&
      !this.draftStale &&
      !this.draftBusy &&
      !this.loadingDrafts
    );
  }
  draftChanged(): void {
    this.draftState.setDirty(this.draftDirty);
    this.draftValidationError = '';
    this.draftNotice = '';
  }
  toggleWorkingDay(day: string, checked: boolean): void {
    if (!this.canEditDraft || !this.draftDefinition) return;
    this.draftDefinition.workingDays = weekdays.filter((value) =>
      value === day
        ? checked
        : this.draftDefinition!.workingDays.includes(value),
    );
    this.draftChanged();
  }
  get knownDraftTimeZone(): boolean {
    return this.timeZones.some(
      (zone) => zone.id === this.draftDefinition?.timeZoneId,
    );
  }
  addHoliday(): void {
    if (!this.canEditDraft || !this.draftDefinition) return;
    if (!dateShape.test(this.holidayDate)) {
      this.draftValidationError = 'Choose a holiday date.';
      return;
    }
    if (this.draftDefinition.holidays.includes(this.holidayDate)) {
      this.draftValidationError = 'This holiday date is already included.';
      return;
    }
    this.draftDefinition.holidays = [
      ...this.draftDefinition.holidays,
      this.holidayDate,
    ].sort();
    this.holidayDate = '';
    this.draftChanged();
  }
  removeHoliday(date: string): void {
    if (!this.canEditDraft || !this.draftDefinition) return;
    this.draftDefinition.holidays = this.draftDefinition.holidays.filter(
      (value) => value !== date,
    );
    this.draftChanged();
  }
  private writeIntent(
    action: string,
    body: unknown,
    draft?: BusinessCalendarDraft,
  ): string {
    const fingerprint = JSON.stringify([action, draft?.id, draft?.eTag, body]);
    if (this.mutationIntent?.fingerprint !== fingerprint)
      this.mutationIntent = {
        fingerprint,
        key:
          globalThis.crypto?.randomUUID?.() ??
          `calendar-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };
    return this.mutationIntent.key;
  }
  private draftInputIdentity(): string {
    return JSON.stringify(this.draftDefinition);
  }
  private adoptDraft(draft: BusinessCalendarDraft): void {
    this.draft = structuredClone(draft);
    this.draftDefinition = structuredClone(draft.definition);
    this.holidayDate = '';
    this.mutationIntent = null;
    this.draftBaseline = this.draftInputIdentity();
    this.showCreate = false;
    this.newCalendarName = '';
    this.draftStale = false;
    this.draftValidationError = '';
    this.draftError = null;
    this.draftState.setDirty(false);
  }
  async openDraft(draft: BusinessCalendarDraft): Promise<void> {
    if (
      !this.canRead() ||
      this.draftBusy ||
      this.draft?.id === draft.id ||
      !(await this.draftState.confirmNavigation())
    )
      return;
    this.adoptDraft(draft);
    this.draftNotice = '';
  }
  async beginCreate(): Promise<void> {
    if (
      !this.canRead() ||
      !this.canAuthor() ||
      this.draftBusy ||
      !(await this.draftState.confirmNavigation())
    )
      return;
    this.draft = null;
    this.draftDefinition = null;
    this.holidayDate = '';
    this.showCreate = true;
    this.newCalendarName = '';
    this.draftError = null;
    this.draftStale = false;
    this.draftNotice = '';
    this.draftState.setDirty(false);
  }
  async closeDraft(): Promise<void> {
    if (!(await this.draftState.confirmNavigation())) return;
    this.draft = null;
    this.draftDefinition = null;
    this.showCreate = false;
    this.newCalendarName = '';
    this.holidayDate = '';
    this.draftError = null;
    this.draftStale = false;
    this.draftNotice = '';
    this.draftState.setDirty(false);
  }
  async editSelected(): Promise<void> {
    const calendar = this.selected;
    if (
      !this.canRead() ||
      !this.canAuthor() ||
      !calendar?.managed ||
      this.draftBusy ||
      !(await this.draftState.confirmNavigation())
    )
      return;
    this.createDraft(calendar.label, calendar.code);
  }
  createCalendar(): void {
    if (
      !this.canRead() ||
      !this.canAuthor() ||
      this.draftBusy ||
      !this.showCreate
    )
      return;
    if (!this.newCalendarName.trim()) {
      this.draftValidationError = 'Enter a calendar name.';
      return;
    }
    this.createDraft(this.newCalendarName.trim());
  }
  private createDraft(displayName: string, calendarCode?: string): void {
    this.setDraftBusy(true);
    this.draftError = null;
    this.draftNotice = '';
    this.mutationRequest = this.api
      .createDraft(
        { displayName, ...(calendarCode ? { calendarCode } : {}) },
        this.writeIntent('create', {
          displayName,
          ...(calendarCode ? { calendarCode } : {}),
        }),
      )
      .subscribe({
        next: (draft) => {
          this.setDraftBusy(false);
          if (
            !validDraft(draft) ||
            draft.statusCode !== 'draft' ||
            (calendarCode && draft.definition.code !== calendarCode)
          ) {
            this.draftContractError();
            return;
          }
          this.adoptDraft(draft);
          this.upsertDraft(draft);
          this.draftNotice =
            'Draft opened. Save its configuration, then publish the exact version when ready.';
        },
        error: (error) => this.draftFailed(error),
      });
  }
  refreshDrafts(reloadId?: number): void {
    if (!this.canRead() || this.draftBusy) return;
    const generation = ++this.draftsGeneration;
    this.draftsRequest?.unsubscribe();
    this.loadingDrafts = true;
    this.draftsRequest = this.api.getDrafts().subscribe({
      next: (drafts) => {
        if (generation !== this.draftsGeneration || !this.canRead()) return;
        this.loadingDrafts = false;
        if (
          !Array.isArray(drafts) ||
          !drafts.every(validDraft) ||
          new Set(drafts.map((draft) => draft.id)).size !== drafts.length
        ) {
          this.draftContractError();
          return;
        }
        this.drafts = drafts;
        const latest =
          this.draft && drafts.find((draft) => draft.id === this.draft!.id);
        if (reloadId !== undefined) {
          const reloaded = drafts.find((draft) => draft.id === reloadId);
          if (reloaded) {
            this.adoptDraft(reloaded);
            this.draftNotice = 'Saved draft reloaded.';
          } else
            this.draftError = {
              title: 'Draft unavailable',
              messages: [
                'The selected draft is no longer available. Your local entries are kept.',
              ],
            };
        } else if (
          this.draft?.statusCode === 'draft' &&
          (!latest ||
            latest.eTag !== this.draft.eTag ||
            latest.revision !== this.draft.revision)
        ) {
          this.draftStale = true;
          this.draftNotice =
            'The saved draft changed. Your local entries are kept; reload the saved draft before saving or publishing.';
        }
      },
      error: (error) => {
        if (generation !== this.draftsGeneration) return;
        this.loadingDrafts = false;
        this.draftError = mapWorkflowRegistryDiagnosticError(error);
        if ([401, 403].includes(this.draftError.statusCode ?? 0)) {
          this.accessDenied = true;
          this.drafts = [];
        }
      },
    });
  }
  async reloadDraft(): Promise<void> {
    if (
      this.draft?.statusCode !== 'draft' ||
      this.draftBusy ||
      !(await this.draftState.confirmNavigation())
    )
      return;
    this.refreshDrafts(this.draft.id);
  }
  refresh(): void {
    this.refreshCatalog();
    this.refreshDrafts();
  }
  saveDraft(): void {
    if (!this.canSaveDraft || !this.draft || !this.draftDefinition) return;
    const holidays = [...this.draftDefinition.holidays];
    if (holidays.some((value) => !dateShape.test(value))) {
      this.draftValidationError = 'Check each holiday date.';
      return;
    }
    if (new Set(holidays).size !== holidays.length) {
      this.draftValidationError = 'Each holiday date must appear once.';
      return;
    }
    if (
      !dateShape.test(this.draftDefinition.effectiveFrom) ||
      (this.draftDefinition.effectiveTo &&
        !dateShape.test(this.draftDefinition.effectiveTo))
    ) {
      this.draftValidationError = 'Enter effective dates as YYYY-MM-DD.';
      return;
    }
    const original = this.draft;
    const definition = {
      ...structuredClone(this.draftDefinition),
      holidays,
      effectiveTo: this.draftDefinition.effectiveTo || null,
    };
    this.setDraftBusy(true);
    this.draftError = null;
    this.draftNotice = '';
    this.mutationRequest = this.api
      .saveDraft(
        original.id,
        original.eTag,
        {
          expectedRevision: original.revision,
          definition,
        },
        this.writeIntent(
          'save',
          { expectedRevision: original.revision, definition },
          original,
        ),
      )
      .subscribe({
        next: (draft) => {
          this.setDraftBusy(false);
          if (!sameDraftVersion(draft, original, 'draft')) {
            this.draftContractError();
            return;
          }
          this.adoptDraft(draft);
          this.upsertDraft(draft);
          this.draftNotice = 'Draft saved. Published versions are unchanged.';
        },
        error: (error) => this.draftFailed(error),
      });
  }
  publishDraft(): void {
    if (!this.canPublishDraft || !this.draft) return;
    const original = this.draft;
    this.setDraftBusy(true);
    this.draftError = null;
    this.draftNotice = '';
    this.mutationRequest = this.api
      .publishDraft(
        original.id,
        original.eTag,
        original.revision,
        this.writeIntent(
          'publish',
          { expectedRevision: original.revision },
          original,
        ),
      )
      .subscribe({
        next: (draft) => {
          this.setDraftBusy(false);
          if (!sameDraftVersion(draft, original, 'published')) {
            this.draftContractError();
            return;
          }
          this.adoptDraft(draft);
          this.upsertDraft(draft);
          this.selectedKey = calendarIdentity(draft.definition);
          this.refreshCatalog();
          this.draftNotice =
            'Exact calendar version published and locked. Earlier versions and recorded calculation evidence are retained. Existing policies keep their selected versions.';
        },
        error: (error) => this.draftFailed(error),
      });
  }
  private upsertDraft(draft: BusinessCalendarDraft): void {
    this.drafts = [
      ...this.drafts.filter((value) => value.id !== draft.id),
      draft,
    ];
  }
  private setDraftBusy(value: boolean): void {
    if (value) {
      this.draftsGeneration++;
      this.draftsRequest?.unsubscribe();
      this.loadingDrafts = false;
    }
    this.draftBusy = value;
    this.draftState.setOperationInProgress(value);
  }
  private draftContractError(): void {
    this.draftError = {
      title: 'Calendar draft contract mismatch',
      messages: [
        'The response does not match this exact calendar draft version. Refresh the saved drafts to inspect the current server state. Your local entries are kept.',
      ],
    };
  }
  private draftFailed(error: unknown): void {
    this.setDraftBusy(false);
    this.draftError = mapWorkflowRegistryDiagnosticError(error);
    this.draftStale = this.draftError.statusCode === 412;
    if ([401, 403].includes(this.draftError.statusCode ?? 0)) {
      this.accessDenied = true;
      this.calendars = [];
      this.drafts = [];
      this.invalidateCalculation();
    }
  }
  get selected(): BusinessCalendar | null {
    const matches = this.calendars.filter(
      (calendar) => this.identity(calendar) === this.selectedKey,
    );
    return matches.length === 1 ? matches[0] : null;
  }
  get holidayRows(): { date: string }[] {
    const calendar = this.selected;
    const source = JSON.stringify(
      calendar
        ? [
            calendar.code,
            calendar.versionCode,
            calendar.contentHash,
            calendar.holidays,
          ]
        : null,
    );
    if (source !== this.holidayRowsSource) {
      this.holidayRowsSource = source;
      this.cachedHolidayRows =
        calendar?.holidays.map((date) => ({ date })) ?? [];
    }
    // EzGrid updates its data signals on a new input reference. Keep this projection
    // stable across change detection and identical catalog refreshes.
    return this.cachedHolidayRows;
  }
  get isDateAddition(): boolean {
    return (
      this.operation === 'add-calendar-days' ||
      this.operation === 'add-working-days'
    );
  }
  get blocked(): boolean {
    return (
      !this.canRead() ||
      this.accessDenied ||
      this.loadingCatalog ||
      this.calculating ||
      this.staleSource ||
      !this.selected
    );
  }
  identity(calendar: BusinessCalendar): string {
    return calendarIdentity(calendar);
  }
  workingDays(calendar: BusinessCalendar): string {
    return weekdays
      .filter((day) => calendar.workingDays.includes(day))
      .map((day) => day[0].toUpperCase() + day.slice(1))
      .join(', ');
  }
  posture(calendar: BusinessCalendar): string {
    return calendar.posture.replace(/[-_]/g, ' ');
  }
  offsetLabel(minutes: number | null | undefined): string {
    if (minutes === null || minutes === undefined) return 'Not applicable';
    const sign = minutes < 0 ? '−' : '+';
    return `UTC${sign}${String(Math.floor(Math.abs(minutes) / 60)).padStart(2, '0')}:${String(Math.abs(minutes) % 60).padStart(2, '0')} (${minutes} minutes)`;
  }
  inputChanged(): void {
    const hadResult = !!this.result || this.calculating;
    this.invalidateCalculation();
    this.validationError = '';
    this.calculationError = null;
    this.notice = hadResult
      ? 'Inputs changed. The previous result is cleared; calculate again for these inputs.'
      : '';
  }
  selectCalendar(): void {
    this.staleSource = false;
    this.inputChanged();
  }
  private invalidateCalculation(): void {
    this.calculationGeneration++;
    this.calculationRequest?.unsubscribe();
    this.calculating = false;
    this.result = null;
  }
  private clearProtectedData(): void {
    this.catalogGeneration++;
    this.catalogRequest?.unsubscribe();
    this.loadingCatalog = false;
    this.calendars = [];
    this.timeZones = [];
    this.draftsGeneration++;
    this.draftsRequest?.unsubscribe();
    this.loadingDrafts = false;
    this.drafts = [];
    this.invalidateCalculation();
  }
  refreshCatalog(): void {
    if (!this.canRead()) return;
    const generation = ++this.catalogGeneration;
    this.catalogRequest?.unsubscribe();
    this.invalidateCalculation();
    this.loadingCatalog = true;
    this.catalogError = null;
    this.calculationError = null;
    this.catalogRequest = this.api.getCatalog().subscribe({
      next: (catalog) => {
        if (generation !== this.catalogGeneration || !this.canRead()) return;
        this.loadingCatalog = false;
        if (
          catalog?.contractVersion !== 1 ||
          !Array.isArray(catalog.calendars) ||
          new Set(catalog.calendars.map(calendarIdentity)).size !==
            catalog.calendars.length
        ) {
          this.calendars = [];
          this.catalogError = {
            title: 'Calendar catalog unavailable',
            messages: [
              'The installed calendar catalog has an unsupported or ambiguous version contract.',
            ],
          };
          return;
        }
        this.calendars = catalog.calendars;
        this.timeZones =
          catalog.timeZones?.filter((zone) => !!zone.id && !!zone.label) ??
          [
            ...new Set([
              'UTC',
              ...catalog.calendars.map((calendar) => calendar.timeZoneId),
            ]),
          ].map((id) => ({ id, label: id }));
        this.accessDenied = false;
        this.staleSource = false;
        if (!this.selectedKey && this.calendars.length)
          this.selectedKey = this.identity(this.calendars[0]);
        this.notice =
          this.selectedKey && !this.selected
            ? 'The selected exact calendar version is unavailable. Choose an installed version explicitly. Your inputs are kept.'
            : '';
      },
      error: (error: unknown) => {
        if (generation !== this.catalogGeneration) return;
        this.loadingCatalog = false;
        this.calendars = [];
        this.catalogError = mapWorkflowRegistryDiagnosticError(error);
        this.accessDenied = [401, 403].includes(
          this.catalogError.statusCode ?? 0,
        );
      },
    });
  }
  calculate(): void {
    if (this.blocked) return;
    const calendar = this.selected!;
    const request: CalendarCalculationRequest = {
      operation: this.operation,
      calendarCode: calendar.code,
      calendarVersionCode: calendar.versionCode,
      expectedCalendarContentHash: calendar.contentHash,
    };
    this.validationError = '';
    if (this.operation === 'instant-to-local') {
      if (!instantShape.test(this.instant))
        this.validationError =
          'Enter an ISO 8601 instant with an explicit Z or UTC offset.';
      else request.instant = this.instant;
    } else {
      if (!dateShape.test(this.date))
        this.validationError = 'Enter a date as YYYY-MM-DD.';
      else request.date = this.date;
      if (this.isDateAddition) {
        if (!Number.isSafeInteger(this.amount))
          this.validationError = 'Enter a whole number of days.';
        else request.amount = this.amount!;
      } else {
        const time = calendarTimeInput(this.time);
        if (!time)
          this.validationError = 'Enter a local time as HH:mm or HH:mm:ss.';
        else request.time = time;
        if (this.explicitOffsetMinutes !== null) {
          if (
            !Number.isInteger(this.explicitOffsetMinutes) ||
            Math.abs(this.explicitOffsetMinutes) > 840
          )
            this.validationError =
              'Enter a whole UTC offset between −840 and 840 minutes, or leave it blank.';
          else request.explicitOffsetMinutes = this.explicitOffsetMinutes;
        }
      }
    }
    this.invalidateCalculation();
    this.calculationError = null;
    this.notice = '';
    if (this.validationError) return;
    const generation = ++this.calculationGeneration,
      fingerprint = this.inputIdentity();
    this.calculating = true;
    this.calculationRequest = this.api.calculate(request).subscribe({
      next: (result) => {
        if (
          generation !== this.calculationGeneration ||
          fingerprint !== this.inputIdentity() ||
          !this.canRead()
        )
          return;
        this.calculating = false;
        if (!matchesCalculation(result, request, calendar)) {
          this.calculationError = {
            title: 'Calculation source mismatch',
            messages: [
              'The result does not match the selected exact calendar, operation and inputs. Refresh the catalog and calculate again. Your inputs are kept.',
            ],
          };
          return;
        }
        this.result = result;
      },
      error: (error: unknown) => {
        if (generation !== this.calculationGeneration) return;
        this.calculating = false;
        this.result = null;
        this.calculationError = mapWorkflowRegistryDiagnosticError(error);
        this.staleSource = this.calculationError.statusCode === 412;
        if ([401, 403].includes(this.calculationError.statusCode ?? 0)) {
          this.accessDenied = true;
          this.calendars = [];
        }
      },
    });
  }
  private inputIdentity(): string {
    return JSON.stringify([
      this.selectedKey,
      this.selected?.contentHash,
      this.operation,
      this.date,
      this.time,
      this.instant,
      this.amount,
      this.explicitOffsetMinutes,
    ]);
  }
}

function validDraft(draft: BusinessCalendarDraft): boolean {
  return (
    !!draft &&
    Number.isSafeInteger(draft.id) &&
    draft.id > 0 &&
    Number.isSafeInteger(draft.revision) &&
    draft.revision > 0 &&
    !!draft.eTag &&
    ['draft', 'published'].includes(draft.statusCode) &&
    !!draft.definition?.code &&
    !!draft.definition.versionCode &&
    draft.definition.posture === 'configured' &&
    Array.isArray(draft.definition.workingDays) &&
    Array.isArray(draft.definition.holidays)
  );
}
function sameDraftVersion(
  draft: BusinessCalendarDraft,
  original: BusinessCalendarDraft,
  statusCode: BusinessCalendarDraft['statusCode'],
): boolean {
  return (
    validDraft(draft) &&
    draft.id === original.id &&
    draft.definition.code === original.definition.code &&
    draft.definition.versionCode === original.definition.versionCode &&
    draft.statusCode === statusCode &&
    draft.revision > original.revision &&
    draft.eTag !== original.eTag
  );
}
function sameInstant(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  return (
    (!left && !right) ||
    (!!left &&
      !!right &&
      instantShape.test(left) &&
      instantShape.test(right) &&
      Number.isFinite(Date.parse(left)) &&
      Date.parse(left) === Date.parse(right))
  );
}
function matchesCalculation(
  result: CalendarCalculationResult,
  request: CalendarCalculationRequest,
  calendar: BusinessCalendar,
): boolean {
  const evidence = result?.evidence;
  if (
    !evidence ||
    result.contractVersion !== 1 ||
    result.sideEffectFree !== true ||
    evidence.operation !== request.operation ||
    evidence.calendarCode !== calendar.code ||
    evidence.calendarVersionCode !== calendar.versionCode ||
    evidence.calendarContentHash !== calendar.contentHash ||
    evidence.timeZoneId !== calendar.timeZoneId ||
    !evidence.ruleCode ||
    !evidence.ruleVersionCode ||
    evidence.effectiveFrom !== calendar.effectiveFrom ||
    (evidence.effectiveTo ?? null) !== (calendar.effectiveTo ?? null) ||
    (evidence.inputDate ?? null) !== (request.date ?? null) ||
    (evidence.inputTime ?? null) !== (request.time ?? null) ||
    !sameInstant(evidence.inputInstant, request.instant) ||
    (evidence.amount ?? null) !== (request.amount ?? null) ||
    (evidence.explicitOffsetMinutes ?? null) !==
      (request.explicitOffsetMinutes ?? null)
  )
    return false;
  if (request.operation === 'local-to-instant')
    return (
      result.valueKind === 'instant' &&
      !!result.instant &&
      sameInstant(result.instant, evidence.resultInstant)
    );
  if (request.operation === 'instant-to-local')
    return (
      result.valueKind === 'local-date-time' &&
      !!result.date &&
      !!result.time &&
      result.date === evidence.resultDate &&
      result.time === evidence.resultTime
    );
  return (
    result.valueKind === 'date-only' &&
    !!result.date &&
    result.date === evidence.resultDate
  );
}
